/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { Button } from "@emisgroup/ui-button";
import { DateEntry } from "@emisgroup/ui-date-picker";
import { Input } from "@emisgroup/ui-input";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { NoExecutionIcon } from "../ExecutionList/NoExecutionIcon";
import {
  Dropdown,
  IListItem
} from "../../../Common/UI/Index";
import { ButtonVariantType, InputType } from "../../../Common/UI/Types";
import { ProgressSpinnerSlice } from "../../../ProgressSpinner/Index";
import { setNotification } from "../../../../utilities/HttpRequest/Script";
import { ErrorAlertSlice } from "../../../ErrorAlert/Index";
import {
  ExecutionResult,
  useCreateExecutionConfiguration,
  useDSAList,
  useUpdateExecutionConfiguration
} from "../../../../service/hooks";
import { setNotificationOpen } from "../../../Notification/redux/Slice";
import { INotification } from "../../../Notification/Index";
import { UniqueId } from "../../../../common/GenerateId";
import { TypesPopup } from "./TypesPopup";
import { RelativeDatePopup } from "./RelativeDatePopup";
import { IExecutionConfiguration, ScreenMode, ISiteConfiguration, ConfigurationType, IDSA, IError } from "./Types";
import { Practices } from "./Practices";
import { MultiSiteEntry } from "./MultiSiteEntry";
import { SiteEntryDialog } from "./SiteEntryDialog";
import { SiteViewDialog } from "./SiteViewDialog";
import { OrganisationCDBField } from "./components/OrganisationCDBField";
import "./Style.scss";
import { FormItem } from "./components/FormItem";
import { RowData } from "./components/MultiSiteTable";
import Add from "~icons/ic/baseline-add-circle";


interface SummaryEntryProps {
  onReturnToListClick: () => void;
  executionConfiguration?: IExecutionConfiguration;
  mode: ScreenMode;
  setMode: (value: (((prevState: ScreenMode) => ScreenMode) | ScreenMode)) => void;
}

const EMPTY_SELECTION_VALUE = " "
const defaultDsa = { text: "Select", value: EMPTY_SELECTION_VALUE };


export const SummaryEntry: React.FC<SummaryEntryProps> = ({ onReturnToListClick,
  executionConfiguration, mode, setMode }) => {
  const { t: translate } = useTranslation();
  const [name, setName] = useState<string>(executionConfiguration ? executionConfiguration.name : "");
  const [type, setType] = useState<string>(executionConfiguration ? executionConfiguration.typeValue : " ");
  const [date, setDate] = useState<Date>(executionConfiguration ?
    new Date(executionConfiguration.relativeDate) : new Date());
  const [time, setTime] = useState<string>(executionConfiguration ? executionConfiguration.relativeTime : "00:00");
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [organisationCdb, setOrganisationCdb] = useState<string>(executionConfiguration?.cdb?.cdbs?? "");
  const [nameErrorText, setNameErrorText] = useState<string>("");
  const [organisationCDBErrorText, setOrganisationCDBErrorText] = useState<string>("");
  const [dsaTypes, setDsaTypes] = useState<IListItem[]>([defaultDsa]);
  const [dsa, setDsa] = useState<string>(executionConfiguration?.dsa?.id ?? defaultDsa.value);
  const [dsaName, setDsaName] = useState<string>(executionConfiguration?.dsa?.name ?? defaultDsa.text);
  const [showSiteDialog, setShowSiteDialog] = useState<boolean>(false);
  const [showSiteViewDialog, setShowSiteViewDialog] = useState<boolean>(false);
  const [sites, setSites] = useState<ISiteConfiguration[]>(executionConfiguration?.sites ?? []);
  const [site, setSite] = useState<ISiteConfiguration>();
  const [addSite, setAddSite] = useState<boolean>(false);
  const [siteMode, setSiteMode] = useState<string>(ScreenMode.ADD);
  const [siteList, setSiteList] = useState<ISiteConfiguration[]>();
  const typeRef = useRef<boolean>(false);
  const cdbFieldRef = useRef<string>("CDB-Field");
  const dsaRef = useRef<boolean>(false);

  const artefactId = sessionStorage.getItem("selectedArtefactId") ?? "";
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const dsaListRequest = useDSAList();

  const getDSAs = (): void => {
    if (dsaListRequest.error) {
      dispatch(ErrorAlertSlice.actions.createErrorAlert({
        title: "Error",
        message: [dsaListRequest.error["errors"][0].message || translate("UnknownError")],
        type: "error"
      }));
    } else {
      const data: IDSA[] = (dsaListRequest.data as ExecutionResult<IDSA[]>).data;
      const dsaList: IListItem[] = [];
      data.map((dsa: IDSA) => {
        dsaList.push({
          text: dsa.name,
          value: dsa.id
        });
      });
      const dsaValue = executionConfiguration?.dsa?.id ?? defaultDsa.value;
      const dsaName = executionConfiguration?.dsa?.name ?? defaultDsa.text;
      setDsa(dsaValue);
      setDsaName(dsaName);
      setDsaTypes((prev): IListItem[] => ([...prev, ...dsaList]));
    }
  };

  React.useEffect(() => {
    if (dsaListRequest.isLoading) {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.DSAList")));
    } else {
      getDSAs();
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
    }
  }, [dsaListRequest.isLoading]);

  const executionCreateRequest = useCreateExecutionConfiguration({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["executionList"] });
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());

      const notification: INotification = {
        id: UniqueId(),
        actionType: "create-execution",
        title: translate("Notification.ExecutionAdded"),
        text: translate("Notification.ToTheList"),
        type: "success",
      };

      dispatch(setNotificationOpen(notification));
      onReturnToListClick();
    },
    onError: (error: IError) => {
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      if (error && error["errors"] && (
        error["errors"][0]?.message?.startsWith("Invalid CDB Number") ||
        (error["errors"][0]?.message?.startsWith("CDB") && error["errors"][0]?.message?.includes("does not exist")))
      ) {
        setOrganisationCDBErrorText(error["errors"][0].message);
      } else {
        dispatch(ErrorAlertSlice.actions.createErrorAlert({
          title: "Error",
          message: [error.errors[0].message || translate("UnknownError")],
          type: "error"
        }));
      }
    },
  });

  const executionUpdateRequest = useUpdateExecutionConfiguration({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["executionList"] });
      queryClient.invalidateQueries({ queryKey: ["executionConfiguration",executionConfiguration?.id] });
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      setNotification(dispatch, "update-execution",
        translate("Notification.UpdateExecutionConfiguration"), "success");
      setMode(ScreenMode.VIEW);
    },
    onError: (error: IError) => {
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      if (error && error["errors"] && error["errors"][0]?.message?.startsWith("Invalid CDB Number")) {
        setOrganisationCDBErrorText(error["errors"][0].message);
      } else {
        dispatch(ErrorAlertSlice.actions.createErrorAlert({
          title: "Error",
          message: [error.errors[0].message || translate("UnknownError")],
          type: "error"
        }));
      }
    },
  });

  const ExecutionTypes: IListItem[] = [
    {
      text: "Select",
      value: EMPTY_SELECTION_VALUE
    },
    {
      text: translate("AddExecution.Type.MultipleSite"),
      value: ConfigurationType.SITES
    },
    {
      text: translate("AddExecution.Type.DSA"),
      value: ConfigurationType.DSA
    },
    {
      text: translate("AddExecution.Type.OrganisationCDB"),
      value: ConfigurationType.CDB
    },
  ];

  const onNameChange = (e): void => {
    const value = e.target["value"];
    if (value.length === 0) {
      setNameErrorText(translate("AddExecution.Name.ErrorText"));
    } else {
      setNameErrorText("");
    }
    setName(value);
  };

  const onTypeChange = (value: string): void => {
    typeRef.current = true;
    dsaRef.current = false;
    setDsaTypes([defaultDsa]);
    if (value !== ConfigurationType.CDB) {
      getDSAs();
    }
    setDsa(defaultDsa.value);
    setDsaName(defaultDsa.text);
    setOrganisationCdb("");
    setOrganisationCDBErrorText("");
    setSites([]);
    setType(value);
  };

  const onDsaChange = (value: string): void => {
    dsaRef.current = true;
    const dsaValue = value !== defaultDsa.value ? value.trim() : defaultDsa.value;
    const dsaName = dsaTypes.find((item) => item.value === value)?.text;
    const dsaValueName = dsaName ? dsaName : defaultDsa.text;
    setDsa(dsaValue);
    setDsaName(dsaValueName);
    if (value.trim() === "") {
      setOrganisationCDBErrorText(translate("AddExecution.DSA.ErrorText"));
    }
  };

  const onAddSiteClick = (): void => {
    setSiteMode(ScreenMode.ADD);
    setAddSite(true);
    setSite({ id: null, expandedKey: UniqueId(), name: "", postcode: "", radius:0.1, practices: [] });
    setShowSiteDialog(true);
  };

  const onSaveSiteValue = (site: ISiteConfiguration): void => {
    if (addSite) {
      setSites([...sites, site]);
      const notification: INotification = {
        id: UniqueId(),
        actionType: "update-execution",
        title: translate("Notification.SiteAdded"),
        text: translate("Notification.ToTheList"),
        type: "success",
      };

      dispatch(setNotificationOpen(notification));
    } else {
      setSites([...sites]);
      const notification: INotification = {
        id: UniqueId(),
        actionType: "update-execution",
        title: translate("Notification.SiteUpdated"),
        text: site.name,
        type: "success",
        closable: true,
      };

      dispatch(setNotificationOpen(notification));
    }
    setShowSiteDialog(false);
  };

  const onRowClicked = (row: RowData): void => {
    setAddSite(false);
    const siteName = row.name as string;
    const selectedSite = sites.find((item) => item.name === siteName.trim());
    setSite(selectedSite);
    setShowSiteViewDialog(true);
  };

  const onRemoveSiteValue = (site: ISiteConfiguration): void => {
    setSites(sites.filter(value => value.name !== site.name));
    setShowSiteViewDialog(false);
    const notification: INotification = {
      id: UniqueId(),
      actionType: "update-execution",
      title: translate("Notification.SiteDeleted"),
      text: site.name,
      type: "success",
      closable: true
    };

    dispatch(setNotificationOpen(notification));
  };

  const onEditSiteValue = (site: ISiteConfiguration): void => {
    setSiteMode(ScreenMode.EDIT);
    setSite(site);
    setShowSiteViewDialog(false);
    setShowSiteDialog(true);
  };

  const onSaveClick = (): void => {
    if (mode === ScreenMode.ADD) {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(
        translate("LoadingMessage.CreateExecutionConfiguration")));
      executionCreateRequest.mutate({
        artefactId,
        name,
        type,
        dsa: { id: dsa, name: dsaName },
        relativeDate: date.toLocaleString("en-GB", { timeZone: "Europe/London" }).substring(0,10) + " " +
            time.toString(),
        cdb: organisationCdb,
        sites: sites
      }, {
        onError: (error) => {
          cdbFieldRef.current = UniqueId();
          if (error && error["errors"]) {
            setOrganisationCDBErrorText(error["errors"][0].message);
          }
        }
      });
    } else {
      const id = executionConfiguration?.id;
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(
        translate("LoadingMessage.CreateExecutionConfiguration")));
      executionUpdateRequest.mutate({
        artefactId,
        id,
        name,
        type,
        dsa: { id: dsa, name: dsaName },
        relativeDate: date.toLocaleString("en-GB", { timeZone: "Europe/London" }).substring(0,10) + " " +
            time.toString(),
        cdb: organisationCdb,
        sites: sites
      },
      {
        onError: (error) => {
          cdbFieldRef.current = UniqueId();
          if (error && error["errors"]) {
            setOrganisationCDBErrorText(error["errors"][0].message);
          }
        }
      }
      );
    }
  };

  useEffect(() => {
    setSiteList(sites);

    // Save button logic
    if (!name || name.length === 0) {
      setSaveDisabled(true);
      return;
    }

    switch (type) {
      case ConfigurationType.CDB:
        setSaveDisabled(organisationCdb.length === 0 || organisationCDBErrorText.length > 0);
        break;
      case ConfigurationType.DSA:
        setSaveDisabled(dsa.trim().length === 0);
        break;
      case ConfigurationType.SITES:
        setSaveDisabled(sites.length === 0 || dsa.trim().length === 0);
        break;
      default:
        setSaveDisabled(true);
    }
  }, [name, type, organisationCdb, organisationCDBErrorText, dsa, sites, translate]);

  React.useEffect(() => {
    if (dsaListRequest.isLoading) {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.DSAList")));
    } else {
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
    }
  }, [dsaListRequest.isLoading]);

  return (
    <div className="summary-entry">
      <div className="summary-entry__fields-flex">
        <div className="summary-entry__fields-form">
          <FormItem
            className="summary-entry__fields__name"
            fieldId="nameField"
            label={translate("AddExecution.Name.Title")}
            errorText={nameErrorText}
            mandatory={true}>
            <Input
              type={InputType.Text}
              name="ExecutionName"
              id="executionNameInput"
              data-testid="executionNameInput"
              required={true}
              value={name}
              onChange={onNameChange}
              maxLength={40}
              invalid={!!nameErrorText}
            />
          </FormItem>
          <FormItem
            className="summary-entry__fields__type"
            fieldId="typeField"
            label={translate("AddExecution.Type.Title")}
            iconRight= {<TypesPopup className={"popup-types-info-add"} />}
            mandatory={true}>
            <Dropdown
              id="executionTypeDropdown"
              data-testid="executionTypeDropdown"
              name="ExecutionType"
              dataSource={ExecutionTypes}
              onChange={onTypeChange}
              disabled={mode === ScreenMode.EDIT}
              value={type}
              invalid={type.trim() === "" && typeRef.current}
            />
          </FormItem>
          <FormItem
            className="summary-entry__fields__dsa"
            fieldId="dsaField"
            label={translate("AddExecution.DSA.Title")}
            mandatory={true}>
            <Dropdown
              id="executionDSADropdown"
              data-testid="executionDSADropdown"
              name="ExecutionDSA"
              onChange={onDsaChange}
              dataSource={dsaTypes}
              defaultValue={defaultDsa.value}
              value={dsa}
              disabled={type === ConfigurationType.CDB}
              invalid={
                (type === ConfigurationType.DSA || type === ConfigurationType.SITES)
                && dsa.trim() === ""
                && dsaRef.current
              }
            />
          </FormItem>
          <div className="summary-entry__fields__relative_date_time">
            <FormItem
              className="summary-entry__fields__relative_date"
              fieldId="dateField"
              label={translate("AddExecution.RelativeDate.Title")}
              iconRight={<RelativeDatePopup className={"popup-relative-time-info-add"} />}
              mandatory={true}>
              <DateEntry
                date={date}
                toDate={new Date().toISOString().split("T")[0]}
                onKeyDown={(event): void => {
                  event.preventDefault();
                }}
                onDateChange={(date): void => setDate(date.date ?? new Date())}
              />
            </FormItem>
            <FormItem
              className="summary-entry__fields__relative_time"
              fieldId="timeField">
              <Input
                type={InputType.Time}
                value={time}
                onChange={(e): void => setTime(e.target.value)}
                onKeyDown={(event): void => {
                  event.preventDefault();
                }}
              />
            </FormItem>
          </div>
        </div>
        {type === ConfigurationType.SITES && (
          <Button
            id="addNewSiteButton"
            data-testid="addNewSiteButton"
            borderless={true}
            type="button"
            onClick={onAddSiteClick}
            disabled={!dsa.trim()}>
            <Add title="add" />
            {translate("Button.AddSite")}
          </Button>
        )}
      </div>
      {!type || type === EMPTY_SELECTION_VALUE &&
            <div className="summary-entry__no-details-center">
              <div className="summary-entry__no-execution-icon">
                <NoExecutionIcon/>
              </div>
              <div className="summary-entry__no-details-main-message">
                {translate("AddExecution.NoDetails.Title")}
              </div>
              <div className="summary-entry__no-details-description">
                {translate("AddExecution.NoDetails.Description")}
              </div>
            </div>
      }
      {
        type === ConfigurationType.SITES &&
          <MultiSiteEntry
            siteList={siteList as ISiteConfiguration[]}
            showSelection= {false}
            onRowClicked={onRowClicked}
          />
      }
      {type === ConfigurationType.DSA &&
        <Practices executionConfiguration={executionConfiguration!} dsa={dsa}/>
      }
      {type === ConfigurationType.CDB && (
        <OrganisationCDBField
          key={cdbFieldRef.current}
          initialValue={organisationCdb}
          errorText={organisationCDBErrorText}
          infoText={translate("AddExecution.OrganisationCDB.Info")}
          label={translate("AddExecution.OrganisationCDB.Title")}
          onChange={(value, error): void => {
            setOrganisationCdb(value);
            setOrganisationCDBErrorText(error);
          }}
        />
      )}
      {
        type === ConfigurationType.SITES && showSiteDialog &&
            <SiteEntryDialog
              onCloseDialog={(): void => { setShowSiteDialog(false); }}
              onSaveDialog={ onSaveSiteValue }
              dsa = {dsaName}
              mode={siteMode}
              site={site as ISiteConfiguration }
              sites={sites}
            />
      }
      {
        type === ConfigurationType.SITES && showSiteViewDialog &&
            <SiteViewDialog
              site={site as ISiteConfiguration }
              onCloseDialog={(): void => { setShowSiteViewDialog(false); }}
              onEditClick={onEditSiteValue} onRemoveClick={onRemoveSiteValue}/>
      }
      <div className="add-execution__save_cancel-container">
        <Button
          id="addExecutionSaveButton"
          data-testid="addExecutionSaveButton"
          disabled={saveDisabled}
          onClick={onSaveClick}
          variant={ButtonVariantType.FILLED}>
          {translate("Button.Save")}
        </Button>
        <Button
          id="addExecutionCancelButton"
          data-testid="addExecutionCancelButton"
          onClick={onReturnToListClick}
          className="add-execution__cancel-button">
          {translate("Button.Cancel")}
        </Button>
      </div>
    </div>
  );
};
